import React from 'react';
import * as Sentry from '@sentry/react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import PagenotfoundImage from 'src/assets/images/general/404.webp';
import { PrimaryButton } from 'src/shared/components/button/Button';
import customersStorageService from 'src/app/customers/services/customers-storage-service';
import projectsStorageService from 'src/app/projects/services/projects-storage-service';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import styles from './ErrorScreen.module.scss';

export const SentryFallbackErrorScreen: Sentry.FallbackRender = errorData => {
    const error = errorData.error as Error;
    return <ErrorScreen error={error} />;
};

type ErrorScreenProps = {
    error: Error;
};

const getErrorMessage = (error: Error) => {
    const errorMessage = error?.message || 'Unknown error';

    // Categorize common errors for better user experience
    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        return {
            title: 'Authentication Error',
            message: 'Your session has expired. Please log in again.',
            action: 'Login Again',
        };
    }

    if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
        return {
            title: 'Access Denied',
            message: "You don't have permission to access this resource.",
            action: 'Go to Dashboard',
        };
    }

    if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        return {
            title: 'Page Not Found',
            message: "The page you're looking for doesn't exist.",
            action: 'Go Home',
        };
    }

    if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        return {
            title: 'Connection Error',
            message: 'Unable to connect to our servers. Please check your internet connection.',
            action: 'Try Again',
        };
    }

    // Default error message
    return {
        title: 'Something went wrong',
        message: 'An unexpected error occurred.',
        action: 'Try Again',
    };
};

const ErrorScreen = (props: ErrorScreenProps) => {
    const error = props.error;
    const navigation = useAppNavigation();
    const errorInfo = getErrorMessage(error);

    const onClick = () => {
        // Clear storage for certain error types
        if (error?.message?.includes('401') || error?.message?.includes('403')) {
            customersStorageService.clearAll();
            projectsStorageService.clearAll();
            navigation.toLogin();
        } else {
            customersStorageService.clearAll();
            projectsStorageService.clearAll();
            navigation.toCustomers();
            window.location.reload();
        }
    };

    const mainContent = (
        <div className={styles.errorBoundaryContainer}>
            <div className={styles.left}>
                <div className={styles.title}>Oops!</div>
                <div className={styles.subTitle}>{errorInfo.title}</div>
                <div className={styles.details}>
                    <ErrorMessage message={errorInfo.message} />
                    {process.env.NODE_ENV === 'development' && (
                        <details style={{ marginTop: '1rem', fontSize: '12px', color: '#666' }}>
                            <summary>Technical Details (Development Only)</summary>
                            <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{error?.stack || error?.message}</pre>
                        </details>
                    )}
                </div>
                <div className={styles.solution}>Please try the action below or contact NatureMetrics support if the issue persists.</div>
                <div className={styles.buttonWrapper}>
                    <PrimaryButton onClick={onClick}>{errorInfo.action}</PrimaryButton>
                </div>
            </div>
            <div className={styles.right}>
                <img src={PagenotfoundImage} alt='Error illustration' />
            </div>
        </div>
    );

    return <StandardAppLayout mainContent={mainContent} />;
};

export default ErrorScreen;
